'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { onlyNumbers } from 'src/helpers/common';
import { getBoolean } from 'src/helpers/common/localStorage';
import { encrypt } from 'src/helpers/crypt/crypt';
import {
  getCreditCardLabel,
  updateHolderName,
  updateMaskCVV,
  updateMaskCard,
  validateCVV,
} from 'src/helpers/masks/cardNumber';
import { updateMask as cpfCnpjMask } from 'src/helpers/masks/cpfCnpj';
import { updateMask as expirationDateMask } from 'src/helpers/masks/expirationDate';
import validators from 'src/helpers/validators';
import useForm from 'src/hooks/useForm';
import type { Address } from 'src/model/Address';
import type CardClient from 'src/model/CardClient';
import type { Cart } from 'src/model/Cart';
import { CorreiosCode } from 'src/model/CorreiosCode';
import { DiscountType } from 'src/model/DiscountType';
import PaymentType from 'src/model/PaymentType';
import StatusType from 'src/model/StatusType';
import type Client from 'src/model/User';
import { setLoading } from 'src/redux/loading/loadingSlice';
import { api } from 'src/services/api';
import { REACT_APP_AUTH } from 'src/utils/config';
import { FormInputNameCreditCard } from '../../types';
import type { DiscountCoupon } from './../../../../model/DiscountCoupon';
import { CheckoutUI } from './ui';
import type { CartView } from '../Cart';
import type { JSX } from 'react/jsx-runtime';
import { trackTikTokEvent } from 'src/pixel/tiktok';

export interface Summary {
  originalPrice: number;
  savings: number;
  productSavings: number;
  couponSavings: number;
  storePickup: number;
  tax: number;
  total: number;
}

interface TotalValueCart {
  totalValue: number;
  totalDiscount: number;
  total: number;
}

export const CheckoutScreen: React.FC = (): JSX.Element => {
  const [signed, setSigned] = useState<boolean>(
    getBoolean(String(REACT_APP_AUTH))
  );
  const dispatch = useDispatch();
  const [addres, setAddress] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>();
  const [carts, setCarts] = useState<Cart[]>([]);
  const [user, setUser] = useState<Client>();
  const [selectedCard, setSelectedCard] = useState<number>();
  const [addNewCard, setAddNewCard] = useState<boolean>(false);
  const [summary, setSummary] = useState<Summary>({
    originalPrice: 0,
    savings: 0,
    productSavings: 0,
    couponSavings: 0,
    storePickup: 0,
    tax: 0,
    total: 0,
  });

  const [cart, setCart] = useState<CartView[]>([]);
  const [card, setCard] = useState<CardClient>();
  const [discountCoupon, setDiscountCoupon] = useState<DiscountCoupon>();
  const [paymentType, setPaymentType] = useState<PaymentType>();
  const [selectedCreditCard, setSelectedCreditCard] = useState<boolean>(false);
  const [selectedPix, setSelectedPix] = useState<boolean>(false);
  const [selectedBoleto, setSelectedBoleto] = useState<boolean>(false);
  const [discount, setDiscount] = useState<number>(0);
  const [totalAfterDiscount, setTotalAfterDiscount] = useState<number>();
  const [selectedInstallments, setSelectedInstallments] = useState<number>(1);
  const [shippingCost, setShippingCost] = useState<number>(0);
  const [selectedDeliveryType, setSelectedDeliveryType] =
    useState<CorreiosCode>(CorreiosCode.SEDEX_CONTRATO);
  const [checkoutBlocked, setCheckoutBlocked] = useState(false);
  const [voucherEligibility, setVoucherEligibility] = useState({
    available: false,
    message: 'Verificando disponibilidade do Voucher...',
  });

  const history = useHistory();

  const isAuth = async (): Promise<void> => {
    setTimeout(async () => {
      if (getBoolean(String(REACT_APP_AUTH)) !== signed) {
        setSigned(getBoolean(String(REACT_APP_AUTH)));
        return;
      } else {
        isAuth();
      }
    }, 500);
  };

  const handleSelectedCard = (id: number, cardSelected: CardClient): void => {
    if (cardSelected && cardSelected) {
      setSelectedCard(id);
      setCard(cardSelected);
    }
  };

  const getUser = async (): Promise<void> => {
    if (signed) {
      dispatch(setLoading(true));
      try {
        const response = await api.get<Client>(`/user/findeone`);
        if (response.status === 200) {
          setUser(response.data);
          if (!response.data.cart || response.data.cart.length === 0) {
            toast.warn('Seu carrinho está vazio.');
            history.replace('/');
            return;
          }
          if (response.data.cart && response.data.cart.length > 0) {
            const cart = response.data.cart;

            setCarts(cart);
            const cartView: CartView[] = [];
            cart.forEach((item) => {
              const id = item.products.id;
              const cartItemId = item.cartItemId;
              const productId = item.products.id;
              const image =
                `${process.env.REACT_APP_BASE_URL}/uploads/` + item.image;
              const name = item.cartProduct.name;
              const nameGrade = item.cartProduct.grade.color
                ? item.cartProduct.grade.color
                : item.cartProduct.grade.name;
              const size = item.cartProduct.grade.size;
              const price = item.cartProduct.grade.price;
              const quantity = item.cartProduct.quantity;
              const store = item.products.storeSeller.name;
              const seller = item.products.storeSeller;
              cartView.push({
                id,
                cartItemId,
                productId,
                image,
                name,
                nameGrade,
                size,
                price,
                quantity,
                store,
                seller,
              });
            });
            setCart(cartView);
          }
          if (response.data.address) {
            setAddress(response.data.address);
          }
        }
      } catch (error) {
        toast.error('Erro ao buscar os dados do usuário.');
      } finally {
        dispatch(setLoading(false));
      }
    } else {
      isAuth();
    }
  };

  const onSubmitAddCreditCard = async (
    e: React.FormEvent,
    addressData?: { selectedAddressId?: string; newAddress?: any },
    installmentData?: { installments: number; totalAmount: number }
  ): Promise<void> => {
    e.preventDefault();
    if (isFormValidAddCreditCard()) {
      if (
        validateCVV(
          formDataAddCreditCard[FormInputNameCreditCard.number],
          formDataAddCreditCard[FormInputNameCreditCard.cvv]
        )
      ) {
        try {
          let addressDataId;
          if (addressData) {
            if (addressData.newAddress) {
              addressData.newAddress.country = 'BR';
              const addressResponse = await api.post(
                '/user/create-address/',
                addressData.newAddress
              );
              addressDataId = addressResponse.data.id;
            } else {
              addressDataId = addressData.selectedAddressId;
            }
          }
          dispatch(setLoading(true));
          const expDate =
            formDataAddCreditCard[FormInputNameCreditCard.date].split('/');
          const card = {
            holder: formDataAddCreditCard[FormInputNameCreditCard.name],
            number: onlyNumbers(
              formDataAddCreditCard[FormInputNameCreditCard.number]
            ),
            expMonth: expDate[0],
            expYear: `20${expDate[1]}`,
            securityCode: formDataAddCreditCard[FormInputNameCreditCard.cvv],
            document: onlyNumbers(
              formDataAddCreditCard[FormInputNameCreditCard.document]
            ),
            cardType: getCreditCardLabel(
              formDataAddCreditCard[FormInputNameCreditCard.number]
            ),
            addressId: addressDataId,
          };

          const payload: any = {
            data: encrypt(JSON.stringify(card)),
          };
          const responseCardClient = await api.post<CardClient>(
            '/client-card',
            payload
          );
          await getUser();
          setTimeout(() => {
            setSelectedCard(
              Number.parseInt(String(responseCardClient.data.id))
            );
          }, 500);
          resetFormAddCreditCard();
          dispatch(setLoading(false));
          toast.success('Cartão adicionado com sucesso!');
        } catch (error: any) {
          toast.error('Erro ao adicionar o cartão:');
          setErrorsAddCreditCard({
            ['document']: ['Algo deu errado'],
          });
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        toast.error('Código de segurança inválido!');
        resetFormAddCreditCard();
      }
    }
  };

  const handleSelectAddress = (id: string): void => {
    setSelectedAddress(id);
    validateLocalRestrictions(id);
  };

  // useEffect(() => {
  //   validateLocalRestrictions(selectedAddress);
  // }, [carts, selectedAddress]);

  const norm = (s: string) =>
    (s ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const canShipLocal = (
    sellerCity: string,
    sellerState: string,
    buyerCity: string,
    buyerState: string,
    buyerCountry?: string
  ) => {
    if (buyerCountry && norm(buyerCountry) !== 'br') return false;
    return (
      norm(sellerCity) === norm(buyerCity) &&
      norm(sellerState) === norm(buyerState)
    );
  };

  const validateLocalRestrictions = (addressId?: string) => {
    if (!addressId) {
      setCheckoutBlocked(true);
      return;
    }
    const addr = addres.find((a) => a.id === addressId);
    if (!addr) {
      setCheckoutBlocked(true);
      return;
    }

    const offenders: string[] = [];
    for (const item of carts) {
      const p = item.products;
      if (!p) continue;
      if (p.saleType !== 'LOCAL') continue;
      const seller = p.storeSeller;
      if (!seller?.city || !seller?.state) {
        offenders.push(p.name ?? 'Produto');
        continue;
      }
      const ok = canShipLocal(
        seller.city,
        seller.state,
        addr.city,
        addr.state,
        addr.country
      );
      if (!ok) offenders.push(p.name ?? 'Produto');
    }

    if (offenders.length > 0) {
      setCheckoutBlocked(true);
      toast.error(
        `Alguns itens do seu carrinho não estão disponíveis para entrega nesse endereço. Remova-os para continuar com a compra.`
      );
    } else {
      setCheckoutBlocked(false);
    }
  };

  const {
    formData: formDataAddCreditCard,
    formErrors: formErrorsAddCreditCard,
    setErrors: setErrorsAddCreditCard,
    onChangeFormInput: onChangeFormInputAddCreditCard,
    isFormValid: isFormValidAddCreditCard,
    resetForm: resetFormAddCreditCard,
  } = useForm({
    initialData: {
      number: '',
      date: '',
      cvv: '',
      name: '',
      document: '',
    },
    validators: {
      number: [validators.required, validators.cardNumber],
      date: [validators.required, validators.cardExpirationDate],
      cvv: [validators.required, validators.cvv],
      name: [validators.required],
      document: [validators.required, validators.cpforcnpj],
    },
    formatters: {
      number: updateMaskCard,
      date: expirationDateMask,
      cvv: updateMaskCVV,
      name: updateHolderName,
      document: cpfCnpjMask,
    },
  });

  const {
    formData: formDataCoupon,
    formErrors: formDataCouponErrors,
    onChangeFormInput: onChangeFormInputCouponCreditCard,
    isFormValid: isFormValidCouponCreditCard,
  } = useForm({
    initialData: {
      code: '',
    },
    validators: {
      code: [validators.required, validators.minLength(6)],
    },
  });

  const handleDiscountCoupon = async (): Promise<void> => {
    const raw = String(formDataCoupon?.code ?? '');
    const code = raw.normalize('NFKC').trim();
    // if (!code || !isFormValidCouponCreditCard()) {
    //   toast.error('Código inválido');
    //   return;
    // }

    dispatch(setLoading(true));
    try {
      const resp = await api.get(
        `/discount-coupon/find/${encodeURIComponent(code).toUpperCase()}`
      );
      const c = resp?.data;

      const valid =
        resp?.status === 200 &&
        c &&
        typeof c.id === 'string' &&
        typeof c.code === 'string' &&
        c.status === 'ACTIVE' &&
        typeof c.discount === 'number' &&
        c.discount > 0 &&
        (c.discountType === 'VALUE' || c.discountType === 'PERCENTAGE') &&
        (typeof c.amount !== 'number' ||
          typeof c.amountSold !== 'number' ||
          c.amountSold < c.amount) &&
        (c.expirationDate === null ||
          (typeof c.expirationDate === 'string' &&
            Number.isFinite(new Date(c.expirationDate).getTime()) &&
            new Date(c.expirationDate).getTime() > Date.now()));

      if (valid) {
        setDiscountCoupon(c);
        setDiscount(c.discount);
        toast.success('Cupom aplicado');
        handleCalculateCartTotal();
      } else {
        setDiscountCoupon(undefined);
        setDiscount(0);
        toast.warn('Cupom inválido ou expirado');
        handleCalculateCartTotal();
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const message =
          error?.response?.data?.message || 'Cupom não encontrado';
        toast.error(message);
      } else {
        toast.error('Erro ao buscar o cupom de desconto. Tente novamente.');
      }
      // toast.error('Erro ao buscar o cupom de desconto:');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnAddCard = (): void => {
    if (addNewCard === true) {
      setAddNewCard(false);
    } else {
      setAddNewCard(true);
    }
  };

  const calculateTotalBuy = (cart: Cart[]): TotalValueCart => {
    let totalValue = 0;
    let totalDiscount = 0;
    let total = 0;

    for (const item of cart) {
      const quantity = Number(item?.cartProduct?.quantity ?? 0);
      const gradeSizeId = item?.cartProduct?.grade?.gradeSizeId;
      if (!quantity || !gradeSizeId) continue;

      let matched: any = null;
      const grades = item?.products?.grade ?? [];
      for (const g of grades) {
        for (const gs of g?.gradeSizes ?? []) {
          if (gs?.id === gradeSizeId) {
            matched = gs;
            break;
          }
        }
        if (matched) break;
      }
      if (!matched?.price) continue;

      const basePrice = Number(matched.price.price ?? 0);
      const discountPercent = Number(matched.price.discount ?? 0);
      const discountValueUnit = (basePrice * discountPercent) / 100;
      const finalPriceUnit = basePrice - discountValueUnit;

      totalValue += finalPriceUnit * quantity;
      totalDiscount += discountValueUnit * quantity;
      total += basePrice * quantity;
    }

    return { totalValue, totalDiscount, total };
  };

  const getInterestRate = (installments: number): number => {
    const rates: Record<number, number> = {
      1: 0,
      2: 0,
      3: 3.49,
      4: 3.99,
      5: 4.49,
      6: 4.99,
      7: 5.49,
      8: 5.99,
      9: 6.49,
      10: 6.99,
      11: 7.49,
      12: 7.99,
    };
    return rates[installments] || 0;
  };

  const calculateFreight = async (): Promise<void> => {
    if (!selectedAddress || !carts || carts.length === 0 || !addres.length) {
      setShippingCost(0);
      return;
    }

    dispatch(setLoading(true));
    try {
      const selectedAddressObj = addres.find(
        (addr) => addr.id === selectedAddress
      );
      if (!selectedAddressObj || !selectedAddressObj.zipcode) {
        toast.error('Endereço de destino ou CEP não encontrado.');
        setShippingCost(0);
        return;
      }

      const productsPayload = carts.map((item) => {
        const productWeight = item.products.dimensions.weight;
        const productLength = item.products.dimensions.depth;
        const productHeight = item.products.dimensions.height;
        const productWidth = item.products.dimensions.width;
        const sellerZipcode = item.products.storeSeller?.cep;
        return {
          origem: onlyNumbers(sellerZipcode),
          peso: String(productWeight),
          tipo: 'CAIXA',
          comprimento: productLength,
          altura: productHeight,
          largura: productWidth,
          diametro: 0,
          declarado: 0,
          adicionais: [{ tipo: 'MAO_PROPRIA' }],
        };
      });

      const freightPayload = {
        type: selectedDeliveryType,
        destino: onlyNumbers(selectedAddressObj.zipcode),
        produtos: productsPayload,
      };

      const response = await api.post(
        '/order/calculate-freight',
        freightPayload
      );

      if (
        response.status === 200 &&
        response.data &&
        typeof response.data.valor === 'number'
      ) {
        setShippingCost(response.data.valor);
      } else {
        toast.error(
          'Erro ao calcular o frete: Resposta inesperada ou valor inválido.'
        );
        setShippingCost(0);
      }
    } catch (error: any) {
      toast.error('Erro ao calcular o frete. Tente novamente.');
      setShippingCost(0);
    } finally {
      dispatch(setLoading(false));
    }
  };

  type Coupon = {
    discountType: DiscountType;
    discount: number;
    status: StatusType;
  };

  const isCouponValid = (c?: Coupon): c is Coupon => {
    if (!c || !Number.isFinite(c.discount) || c.discount <= 0) return false;

    const status = String((c as any).status).toUpperCase();
    const dtype = String((c as any).discountType).toUpperCase();

    return status === 'ACTIVE' && (dtype === 'VALUE' || dtype === 'PERCENTAGE');
  };

  const computeCouponValue = (baseTotal: number, c: Coupon): number => {
    const base = Math.max(baseTotal, 0);
    const dtype = String((c as any).discountType).toUpperCase();

    if (dtype === 'VALUE') {
      return Math.min(c.discount, base);
    }
    return Math.min((base * c.discount) / 100, base);
  };

  const handleCalculateCartTotal = (): void => {
    const { totalValue, totalDiscount, total } = calculateTotalBuy(carts);

    let baseTotal = Math.max(totalValue, 0);

    let couponApplied = 0;
    if (isCouponValid(discountCoupon)) {
      couponApplied = computeCouponValue(baseTotal, discountCoupon);
      baseTotal = Math.max(baseTotal - couponApplied, 0);
    }

    const finalTotal = baseTotal + shippingCost;

    setTotalAfterDiscount(finalTotal);

    setSummary({
      originalPrice: total,
      savings: totalDiscount + couponApplied,
      productSavings: totalDiscount,
      couponSavings: couponApplied,
      storePickup: shippingCost,
      tax: 0,
      total: finalTotal,
    });
  };

  useEffect(() => {
    if (!selectedAddress || cart.length === 0) return;

    const buyerAddress = addres.find((addr) => addr.id === selectedAddress);
    const seller = cart[0].seller;
    const local = Boolean(
      buyerAddress &&
        seller?.city &&
        seller?.state &&
        canShipLocal(
          seller.city,
          seller.state,
          buyerAddress.city,
          buyerAddress.state,
          buyerAddress.country
        )
    );

    if (local && selectedDeliveryType !== CorreiosCode.ENTREGA_LOCAL) {
      setSelectedDeliveryType(CorreiosCode.ENTREGA_LOCAL);
      setShippingCost(0);
      return;
    }

    if (!local && selectedDeliveryType === CorreiosCode.ENTREGA_LOCAL) {
      setSelectedDeliveryType(CorreiosCode.SEDEX_CONTRATO);
      return;
    }

    if (selectedDeliveryType !== CorreiosCode.ENTREGA_LOCAL) {
      calculateFreight();
    }
  }, [selectedAddress, cart, addres, selectedDeliveryType]);

  const handlePayment = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      if (!selectedAddress) {
        toast.error('Selecione um endereço de entrega');
        return;
      }
      if (!paymentType) {
        toast.error('Selecione um método de pagamento');
        return;
      }
      if (!carts || carts.length === 0) {
        toast.error('Carrinho vazio');
        return;
      }
      if (
        paymentType === PaymentType.VOUCHER &&
        selectedDeliveryType !== CorreiosCode.ENTREGA_LOCAL
      ) {
        toast.error(
          'Voucher está disponível somente para entrega local no estabelecimento.'
        );
        return;
      }
      if (
        paymentType === PaymentType.VOUCHER &&
        !voucherEligibility.available
      ) {
        toast.error(voucherEligibility.message);
        return;
      }
      if (checkoutBlocked) {
        toast.error(
          'Não é possível prosseguir: há produto LOCAL incompatível com o endereço.'
        );
        dispatch(setLoading(false));
        return;
      }
      const productsCart = carts
        .map((cart) => {
          if (!cart.products || !cart.cartProduct) return null;
          return {
            id: cart.products.id,
            name: cart.products.name,
            quantity: String(cart.cartProduct.quantity),
            grade: {
              gradeSizeId: cart.cartProduct.grade.gradeSizeId,
            },
          };
        })
        .filter(Boolean);

      const finalTotalForApi = summary?.total + shippingCost || 0;

      let paymentData: any = {
        paymentMethod: paymentType,
      };

      if (paymentType === PaymentType.CREDIT_CARD && card) {
        const expirationDate = card.cardExpirationDate.split('/');
        paymentData = {
          ...paymentData,
          installments: selectedInstallments,
          clientCardId: card.id,
          cardSameUser: 1,
        };
      } else if (paymentType === PaymentType.PIX) {
        paymentData = {
          ...paymentData,
          installments: 1,
        };
      } else if (
        paymentType === PaymentType.BOLETO ||
        paymentType === PaymentType.VOUCHER
      ) {
        paymentData = {
          ...paymentData,
          installments: 1,
        };
      }

      const payload = {
        totalValue: calculateTotalBuy(carts).totalValue,
        addressId: selectedAddress,
        products: productsCart,
        payments: paymentData,
        delivery: {
          type: selectedDeliveryType,
          price: shippingCost,
          date: new Date().toISOString().slice(0, 10),
        },
        ...(discountCoupon && {
          discountCoupon:
            paymentType === PaymentType.VOUCHER
              ? discountCoupon.code
              : {
                  id: discountCoupon.id,
                  code: discountCoupon.code,
                  discount: discount,
                  discountType: discountCoupon.discountType,
                },
        }),
      };

      console.log(payload);

      const response = await api.post('/order', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success('Pedido realizado com sucesso');

        const contentIds = carts.map((cart) => cart.products.id);
        const contents = carts.map((cart) => ({
          id: cart.products.id,
          quantity: cart.cartProduct.quantity,
          item_price: cart.cartProduct.grade.price,
        }));

        const totalValue = contents.reduce((sum, item) => {
          return sum + item.item_price * item.quantity;
        }, 0);

        const shippingValue = Number(shippingCost) || 0;
        const finalValue = totalValue + shippingValue;

        trackTikTokEvent('CompletePayment', {
          content_ids: contentIds,
          contents: contents,
          value: Number(finalValue).toFixed(2),
          currency: 'BRL',
          order_id: response.data.id || response.data.orderId,
          payment_method: paymentType,
          shipping_cost: shippingValue,
        });
        if (
          paymentType === PaymentType.PIX ||
          paymentType === PaymentType.BOLETO
        ) {
          localStorage.setItem('paymentData', JSON.stringify(response.data));
          history.push('/payment/');
        } else {
          history.push('/user-profile?tab=acompanhar-pedidos');
        }
      } else {
        toast.error('Erro ao processar pedido');
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        const message = error.response.data.message || 'Cupom não encontrado';
        toast.error(message);
      } else {
        toast.error(
          'Ocorreu um erro ao finalizar sua compra. Tente novamente mais tarde'
        );
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePaymentType = (type: PaymentType): void => {
    setPaymentType(type);
    setSelectedCreditCard(type === PaymentType.CREDIT_CARD);
    setSelectedPix(type === PaymentType.PIX);
    setSelectedBoleto(type === PaymentType.BOLETO);
    if (type !== PaymentType.CREDIT_CARD) setSelectedInstallments(1);
  };

  useEffect(() => {
    isAuth();
  }, [signed]);

  useEffect(() => {
    getUser();
  }, [signed]);

  useEffect(() => {
    handleCalculateCartTotal();
  }, [carts, discountCoupon, paymentType, selectedInstallments, shippingCost]);

  useEffect(() => {
    let active = true;
    const checkVoucherEligibility = async (): Promise<void> => {
      if (!carts.length) {
        if (active)
          setVoucherEligibility({
            available: false,
            message: 'Carrinho vazio',
          });
        return;
      }
      try {
        const response = await api.post('/order/voucher/eligibility', {
          productIds: carts.map((item) => item.products.id),
          deliveryType: selectedDeliveryType,
          addressId: selectedAddress,
        });
        if (active) setVoucherEligibility(response.data);
      } catch {
        if (active)
          setVoucherEligibility({
            available: false,
            message: 'Não foi possível validar o Voucher neste momento.',
          });
      }
    };
    checkVoucherEligibility();
    return () => {
      active = false;
    };
  }, [carts, selectedDeliveryType, selectedAddress]);

  return user ? (
    <CheckoutUI
      formData={formDataAddCreditCard}
      formErrors={formErrorsAddCreditCard}
      onChangeFormInput={onChangeFormInputAddCreditCard}
      onAddCard={handleOnAddCard}
      addNewCard={addNewCard}
      selectedCard={selectedCard}
      onSelectCard={handleSelectedCard}
      onSubmitRegister={onSubmitAddCreditCard}
      user={user}
      cartView={cart}
      carts={carts}
      addresses={addres}
      selectedAddress={selectedAddress}
      onSelectAddress={handleSelectAddress}
      checkoutBlocked={checkoutBlocked}
      summary={summary}
      onPayment={handlePayment}
      onApplyCoupon={handleDiscountCoupon}
      onSelectType={handlePaymentType}
      selectedCreditCard={selectedCreditCard}
      selectedPix={selectedPix}
      selectedBoleto={selectedBoleto}
      discount={discount}
      totalAfterDiscount={totalAfterDiscount}
      formDataCoupon={formDataCoupon}
      formDataCouponErrors={formDataCouponErrors}
      onChangeFormCouponInput={onChangeFormInputCouponCreditCard}
      shippingCost={shippingCost}
      selectedDeliveryType={selectedDeliveryType}
      onSelectDeliveryType={setSelectedDeliveryType}
      selectedInstallments={selectedInstallments}
      onSelectInstallments={(n: number) => setSelectedInstallments(n)}
      voucherAvailable={voucherEligibility.available}
      voucherMessage={voucherEligibility.message}
    />
  ) : (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Carrinho Vazio
        </h3>
        <p className="text-gray-500">
          Adicione produtos ao seu carrinho para continuar
        </p>
      </div>
    </div>
  );
};
